import React, { useCallback, useEffect, useState, createContext, useContext } from 'react';
import { supabaseClient, isSupabaseConfigured } from '../lib/supabase';
import { useAuth } from './AuthContext';
export interface EmployeeBalance {
  id: string;
  employeeId: string;
  companyId: string;
  month: number;
  year: number;
  assignedAmount: number;
  currentBalance: number;
  assignedBy: string;
  assignedAt: string;
  modifiedBy?: string;
  modifiedAt?: string;
  notes?: string;
}
export interface BalanceLog {
  id: string;
  companyId?: string;
  companyName?: string;
  employeeId?: string;
  employeeName?: string;
  actionType: string;
  amount?: number;
  previousAmount?: number;
  month?: number;
  year?: number;
  performedBy: string;
  performedByRole?: string;
  notes?: string;
  createdAt: string;
}
interface BalanceContextType {
  currentBalance: EmployeeBalance | null;
  loading: boolean;
  refreshBalance: () => Promise<void>;
  hasBalance: boolean;
  canUseBalance: (amount: number) => boolean;
  consumeBalance: (amount: number, performedBy: string) => Promise<boolean>;
}
const BalanceContext = createContext<BalanceContextType | undefined>(undefined);
export function BalanceProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    user
  } = useAuth();
  const [currentBalance, setCurrentBalance] = useState<EmployeeBalance | null>(null);
  const [loading, setLoading] = useState(false);
  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();
  const loadBalance = useCallback(async () => {
    if (!user || user.role !== 'employee' || !user.employeeId) return;
    if (!isSupabaseConfigured()) return;
    setLoading(true);
    try {
      const balance = await supabaseClient.getEmployeeBalance(user.employeeId, currentMonth, currentYear);
      setCurrentBalance(balance);
    } catch (e) {
      console.error('[BalanceContext] Error loading balance:', e);
      setCurrentBalance(null);
    } finally {
      setLoading(false);
    }
  }, [user?.employeeId, currentMonth, currentYear]);
  useEffect(() => {
    loadBalance();
  }, [loadBalance]);
  // Real-time subscription — filtered to this employee only
  useEffect(() => {
    if (!user?.employeeId || !isSupabaseConfigured()) return;
    let subscription: any = null;
    try {
      subscription = supabaseClient.subscribeToBalances(() => {
        loadBalance();
      });
    } catch (e) {
      // Silent fail
    }
    return () => {
      subscription?.unsubscribe();
    };
  }, [user?.employeeId, loadBalance]);
  // NOTE: Polling removed — real-time subscription handles live updates.
  // Visibility change below covers the multi-tab stale balance scenario.
  // Refresh when tab becomes visible again (critical for multi-tab scenario)
  // If user spent balance in another tab, this tab will refresh immediately on focus
  useEffect(() => {
    if (!user?.employeeId) return;
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadBalance();
      }
    };
    // NOTE: Removed window 'focus' listener — it fired on every click/interaction
    // causing constant re-renders. visibilitychange is sufficient for multi-tab sync.
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user?.employeeId, loadBalance]);
  const hasBalance = currentBalance !== null && currentBalance.currentBalance > 0;
  const canUseBalance = useCallback((amount: number) => {
    if (!currentBalance) return false;
    return currentBalance.currentBalance >= amount;
  }, [currentBalance]);
  const consumeBalance = useCallback(async (amount: number, performedBy: string): Promise<boolean> => {
    if (!user?.employeeId || !currentBalance) return false;
    if (currentBalance.currentBalance < amount) return false;
    try {
      await supabaseClient.consumeBalance(user.employeeId, currentMonth, currentYear, amount);
      // Log the consumption
      await supabaseClient.createBalanceLog({
        employeeId: user.employeeId,
        employeeName: user.name,
        companyId: currentBalance.companyId,
        actionType: 'balance_consumed',
        amount,
        previousAmount: currentBalance.currentBalance,
        month: currentMonth,
        year: currentYear,
        performedBy,
        performedByRole: 'employee',
        notes: `Consumo automático por pedido`
      }).catch(() => {}); // Non-critical
      await loadBalance();
      return true;
    } catch (e) {
      console.error('[BalanceContext] Error consuming balance:', e);
      return false;
    }
  }, [user, currentBalance, currentMonth, currentYear, loadBalance]);
  return <BalanceContext.Provider value={{
    currentBalance,
    loading,
    refreshBalance: loadBalance,
    hasBalance,
    canUseBalance,
    consumeBalance
  }} data-id="element-1029">
      {children}
    </BalanceContext.Provider>;
}
export function useBalance() {
  const context = useContext(BalanceContext);
  if (context === undefined) {
    throw new Error('useBalance must be used within a BalanceProvider');
  }
  return context;
}
// Month names in Spanish
export const MONTH_NAMES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
export const formatMonth = (month: number, year: number) => `${MONTH_NAMES[month - 1]} ${year}`;